import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Plus, Trash2, Eye, EyeOff, Save } from "lucide-react";

const field = "w-full border border-[#E5E5E5] bg-white px-4 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-restrained-blue";

export default function LeadershipAdmin() {
  const [leaders, setLeaders] = useState([]);
  const load = () => api.get("/admin/leaders").then(({ data }) => setLeaders(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/admin/leaders", { name: "", title: "", bio: "", order: leaders.length }); load(); }
    catch { toast.error("Failed"); }
  };
  const save = async (l) => {
    try { await api.put(`/admin/leaders/${l.id}`, l); toast.success("Saved"); load(); }
    catch { toast.error("Save failed"); }
  };
  const publish = async (l) => {
    try { await api.post(`/admin/leaders/${l.id}/publish`); toast.success("Published"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const unpublish = async (l) => { await api.post(`/admin/leaders/${l.id}/unpublish`); toast.success("Unpublished"); load(); };
  const remove = async (l) => { if (!window.confirm("Delete this profile?")) return; await api.delete(`/admin/leaders/${l.id}`); toast.success("Deleted"); load(); };

  const setField = (id, k, v) => setLeaders((ls) => ls.map((l) => (l.id === id ? { ...l, [k]: v } : l)));

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="font-serif text-4xl text-deep-navy">Leadership</h1><p className="text-charcoal/60">Profiles are unpublished until verified.</p></div>
        <button onClick={create} data-testid="leader-create" className="inline-flex items-center gap-2 bg-deep-navy text-white px-5 py-2.5 text-sm hover:bg-restrained-blue transition-colors"><Plus size={16} /> Add profile</button>
      </div>

      {leaders.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E5E5E5] p-12 text-center text-charcoal/50">No leadership profiles yet. Add one when details are confirmed.</div>
      ) : (
        <div className="space-y-5" data-testid="leaders-admin-list">
          {leaders.map((l) => (
            <div key={l.id} className="bg-white border border-[#E5E5E5] p-6">
              <div className="flex items-center justify-between mb-4">
                <span className={`text-xs px-2 py-0.5 ${l.status === "published" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>{l.status}</span>
                <div className="flex gap-2">
                  <button onClick={() => save(l)} className="inline-flex items-center gap-1 border border-[#E5E5E5] px-3 py-1.5 text-xs hover:bg-off-white"><Save size={13} /> Save</button>
                  {l.status === "published"
                    ? <button onClick={() => unpublish(l)} className="inline-flex items-center gap-1 border border-[#E5E5E5] px-3 py-1.5 text-xs hover:bg-off-white"><EyeOff size={13} /> Unpublish</button>
                    : <button onClick={() => publish(l)} data-testid={`leader-publish-${l.id}`} className="inline-flex items-center gap-1 border border-[#E5E5E5] px-3 py-1.5 text-xs hover:bg-off-white"><Eye size={13} /> Publish</button>}
                  <button onClick={() => remove(l)} className="text-charcoal/30 hover:text-destructive p-1.5"><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input className={field} placeholder="Full name *" value={l.name || ""} onChange={(e) => setField(l.id, "name", e.target.value)} />
                <input className={field} placeholder="Title *" value={l.title || ""} onChange={(e) => setField(l.id, "title", e.target.value)} />
                <input className={field} placeholder="Photo URL" value={l.photo || ""} onChange={(e) => setField(l.id, "photo", e.target.value)} />
                <input className={field} placeholder="LinkedIn URL" value={l.linkedin || ""} onChange={(e) => setField(l.id, "linkedin", e.target.value)} />
              </div>
              <textarea className={`${field} mt-3 resize-none`} rows={3} placeholder="Biography *" value={l.bio || ""} onChange={(e) => setField(l.id, "bio", e.target.value)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
